import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Wind, Plus, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { useUpdateApartment } from "@/hooks/use-resources";
import { api } from "@shared/routes";

type LaundryConfigItem = { name: string; defaultQty: number };

export function ApartmentLaundryItemsSection({ apartmentId, apartment }: { apartmentId: number; apartment: any }) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const updateApartment = useUpdateApartment();

  const saved: LaundryConfigItem[] = apartment?.laundryItems ?? [];
  const [items, setItems] = useState<LaundryConfigItem[]>(saved);
  const [newName, setNewName] = useState("");
  const [newQty, setNewQty] = useState(1);

  const isDirty = JSON.stringify(items) !== JSON.stringify(saved);

  const addItem = () => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    if (items.some(i => i.name.toLowerCase() === trimmed.toLowerCase())) {
      toast({ title: "Item already exists", variant: "destructive" }); return;
    }
    setItems(prev => [...prev, { name: trimmed, defaultQty: Math.max(1, newQty) }]);
    setNewName("");
    setNewQty(1);
  };

  const removeItem = (idx: number) => setItems(prev => prev.filter((_, i) => i !== idx));

  const updateQty = (idx: number, qty: number) =>
    setItems(prev => prev.map((item, i) => i === idx ? { ...item, defaultQty: Math.max(1, qty) } : item));

  const save = () => {
    updateApartment.mutate(
      { id: apartmentId, data: { laundryItems: items } as any }, // laundryItems is a new jsonb field not yet in the strict InsertApartment type
      {
        onSuccess: () => {
          qc.invalidateQueries({ queryKey: [api.apartments.get.path.replace(":id", String(apartmentId))] });
          toast({ title: "Laundry items saved" });
        },
        onError: (e: Error) => toast({ title: "Error", description: e.message, variant: "destructive" }),
      },
    );
  };

  return (
    <div className="space-y-4 py-4">
      <div className="flex items-center gap-2">
        <Wind className="w-4 h-4 text-teal-600" />
        <h3 className="font-medium">Laundry Items</h3>
      </div>
      <p className="text-sm text-muted-foreground">
        Configure the laundry items for this unit. Cleaners will select from this list when logging laundry intake.
      </p>

      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground italic">No items configured yet.</p>
      ) : (
        <div className="space-y-2">
          <div className="grid grid-cols-[1fr_100px_40px] gap-2 text-xs font-medium text-muted-foreground px-1">
            <span>Item name</span>
            <span className="text-center">Default qty</span>
            <span />
          </div>
          {items.map((item, i) => (
            <div key={i} className="grid grid-cols-[1fr_100px_40px] items-center gap-2 bg-muted/30 rounded-md px-3 py-2">
              <span className="text-sm font-medium">{item.name}</span>
              <Input
                type="number"
                min={1}
                value={item.defaultQty}
                onChange={e => updateQty(i, Number(e.target.value))}
                className="h-8 text-center"
              />
              <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => removeItem(i)}>
                <Trash2 className="w-3.5 h-3.5" />
              </Button>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-2 items-center border rounded-md p-3 bg-muted/20">
        <Input
          placeholder="Item name (e.g. Towels)"
          value={newName}
          onChange={e => setNewName(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter") addItem(); }}
          className="flex-1 h-8"
        />
        <Input
          type="number"
          min={1}
          value={newQty}
          onChange={e => setNewQty(Number(e.target.value))}
          className="w-20 h-8 text-center"
        />
        <Button size="sm" variant="outline" onClick={addItem} disabled={!newName.trim()}>
          <Plus className="w-3.5 h-3.5 mr-1" /> Add
        </Button>
      </div>

      {isDirty && (
        <div className="flex justify-end">
          <Button onClick={save} disabled={updateApartment.isPending}>
            {updateApartment.isPending ? "Saving…" : "Save Changes"}
          </Button>
        </div>
      )}
    </div>
  );
}
